import axios from 'axios';
import { CoHostPersonality, StudioConfig, ArticleSelectionItem } from 'types/studio.types';
import { API_ENDPOINTS } from './endPoints';

const axiosParam = {
  baseURL: process.env.REACT_APP_API_URL,
  withCredentials: true, // Important for cookies/sessions
  headers: {
    'Content-Type': 'application/json',
  },
  ...(process.env.REACT_APP_USER ? {
    params: {
      forceUser: JSON.parse(process.env.REACT_APP_USER || '{}')?.email,
    }
  } : {})
}

console.log('axiosParam:', axiosParam);

const apiClient = axios.create(axiosParam);

// Enable opt-in request logging via env var (default off)
const LOG_AXIOS_REQUESTS = (() => {
  const v = process.env.REACT_APP_AXIOS_LOG_REQUESTS;
  if (!v) return false;
  return v === '1' || v.toLowerCase() === 'true';
})();

// Add request interceptor for handling auth tokens if needed
apiClient.interceptors.request.use(
  (config) => {
    // Optional: log method and resolved URL before request is sent
    if (LOG_AXIOS_REQUESTS) {
      try {
        const method = (config.method || 'GET').toUpperCase();
        const uri = apiClient.getUri(config);
        // eslint-disable-next-line no-console
        console.log(`[axios] ${method} ${uri}`);
      } catch {}
    }
    // You can add auth headers here if needed
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for handling errors
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Handle common errors like 401 Unauthorized
    if (error.response && error.response.status === 401) {
      // Redirect to login or refresh token logic
      window.location.href = '/';
    }
    return Promise.reject(error);
  }
);

interface GenerateVoiceSampleResponse {
  audio: Blob;
  voiceId: string;
  voiceText: string;
  headers: any; // Include all response headers
};

// Helper to decode base64 (including potential base64url) into a UTF-8 string in the browser
const decodeBase64Utf8 = (b64?: string | null): string => {
  if (!b64 || typeof b64 !== 'string') return '';
  try {
    // Normalize base64url variants
    let normalized = b64.replace(/-/g, '+').replace(/_/g, '/');
    while (normalized.length % 4 !== 0) normalized += '=';
    const binary = atob(normalized);
    const bytes = Uint8Array.from(binary, c => c.charCodeAt(0));
    return new TextDecoder().decode(bytes);
  } catch (e) {
    console.error('Failed to decode base64 header (x-voice-text):', e);
    return '';
  }
};

export const generateVoiceSample = async (
  websitePK: string,
  voiceOrHost: string | CoHostPersonality,
  text: string
): Promise<GenerateVoiceSampleResponse> => {
  try {
    const endpoint = API_ENDPOINTS.PUBLISHER.WEBSITES.STUDIO.VOICE_SAMPLE(websitePK);

    // Build request payload supporting either a voiceId or a full host object
    let requestBody: any = { websitePK, text };
    if (typeof voiceOrHost === 'string') {
      requestBody.voiceId = voiceOrHost;
    } else if (voiceOrHost && typeof voiceOrHost === 'object') {
      requestBody.host = voiceOrHost;
    }

    const response = await apiClient({
      method: 'post',
      url: endpoint,
      data: requestBody,
      responseType: 'blob',
    });

    const voiceTextHeader = response.headers['x-voice-text'];
    const voiceText = decodeBase64Utf8(voiceTextHeader);

    const voiceSampleResponse = {
      audio: response.data,
      voiceId: response.headers['x-voice-id'],
      voiceText,
      headers: response.headers, // Include all response headers
    }
    return voiceSampleResponse;
  } catch (error) {
    console.error('Voice sample generation error:', error);
    throw error;
  }
};

export interface GenerateNewsPreviewResponse {
  audio?: Blob; // optional now (only for wav mode)
  hlsUrl?: string; // new for hls streaming mode
  generationMode?: 'wav' | 'hls';
  newsScript?: any; // Parsed script JSON (legacy top-level script, if any)
  metadata?: any; // Metadata returned by server
  articleIds?: any; // Article IDs returned by server
  headers?: any; // Real response headers only (no legacy construction)
  // NEW: hierarchical chapters returned by the server
  chapters?: any[] | null;
}

// NEW: Job structures for async studio preview
export type JobStatus = 'pending' | 'running' | 'succeeded' | 'failed' | 'canceled';

export interface StudioPreviewJobStepLogEntry {
  timestamp: string;
  message: string;
}

export interface StudioPreviewJobResultPayload {
  generationMode?: 'wav' | 'hls';
  hlsUrl?: string;
  audioBase64?: string;
  metadata?: any;
  articleIds?: any;
  script?: any;
  chapters?: any[] | null;
  [key: string]: any;
}

export interface StudioPreviewJob {
  jobId: string;
  type: string;
  websiteId?: string;
  status: JobStatus;
  createdAt?: string;
  startedAt?: string;
  finishedAt?: string;
  currentStep?: string;
  stepLog?: StudioPreviewJobStepLogEntry[];
  result?: StudioPreviewJobResultPayload | null;
  error?: any;
}

export interface EnqueueStudioPreviewResponse {
  jobId: string;
  status: JobStatus;
  type: string;
}

// Helper to map a final job.result payload into the existing GenerateNewsPreviewResponse shape
export const mapStudioPreviewResultToGenerateNewsPreviewResponse = (
  result: StudioPreviewJobResultPayload,
  headers?: any,
  requestedMode: 'wav' | 'hls' = 'hls'
): GenerateNewsPreviewResponse => {
  const generationMode: 'wav' | 'hls' =
    (result.generationMode as 'wav' | 'hls') || (result.hlsUrl ? 'hls' : requestedMode);

  let audioBlob: Blob | undefined = undefined;
  if (generationMode === 'wav' && result.audioBase64) {
    try {
      const wavBytes = Uint8Array.from(atob(result.audioBase64), c => c.charCodeAt(0));
      audioBlob = new Blob([wavBytes], { type: 'audio/wav' });
    } catch (e) {
      console.error('Error decoding audioBase64 from job result:', e);
      audioBlob = new Blob([], { type: 'audio/wav' });
    }
  }

  const hlsUrl: string | undefined = generationMode === 'hls' ? result.hlsUrl : undefined;
  const newsScript = (result as any).script ?? null;
  const responseChapters = Array.isArray(result.chapters) ? result.chapters : null;

  return {
    generationMode,
    audio: audioBlob,
    hlsUrl,
    newsScript,
    metadata: result.metadata ?? null,
    articleIds: result.articleIds ?? null,
    headers,
    chapters: responseChapters,
  };
};

// Updated: enqueue async preview job instead of waiting for full result synchronously
export const generateNewsPreview = async (
  websitePK: string,
  studioConfig: StudioConfig,
  numArticles: number = 2,
  generationModeRequest: 'wav' | 'hls' = 'hls',
  chapters?: any[],
  articleIds?: ArticleSelectionItem[],
  podcastType?: 'multiple_articles' | 'emergency',
  classification?: any
): Promise<EnqueueStudioPreviewResponse> => {
  try {
    const endpoint = API_ENDPOINTS.PUBLISHER.WEBSITES.STUDIO.PREVIEW(websitePK);

    const requestBody: any = {
      websitePK,
      studioConfig,
      responseMode: generationModeRequest,
      dontAutoDelete: true,
      podcastType,
      classification,
    };

    if (articleIds && articleIds.length > 0) {
      requestBody.articleIds = articleIds;
    } else {
      requestBody.numArticles = numArticles;
    }

    if (chapters && Array.isArray(chapters)) {
      requestBody.chapters = chapters;
    }

    const response = await apiClient.post(endpoint, requestBody);
    const data = response.data || {};

    return {
      jobId: data.jobId,
      status: data.status as JobStatus,
      type: data.type,
    };
  } catch (error: any) {
    if (error?.response?.data) {
      console.error('Generate news preview enqueue error (api):', error.response.data);
    } else {
      console.error('Generate news preview enqueue error:', error);
    }
    throw error;
  }
};

// New: Redeem a share by ID
export interface RedeemShareResponse {
  id: string;
  recipientName: string;
  recipientEmail: string;
  message: string;
  ttl: number;
  expirationDate: string; // ISO date-time
  hlsUrl: string;
  createdAt?: string; // ISO date-time
  updatedAt?: string; // ISO date-time
}

export const redeemShare = async (id: string): Promise<RedeemShareResponse> => {
  try {
    const endpoint = API_ENDPOINTS.NOAUTH.REDEEM_SHARE;
    const response = await apiClient.post(endpoint, { id });
    return response.data as RedeemShareResponse;
  } catch (error) {
    console.error('Redeem share error:', error);
    throw error;
  }
};

export const generateArticleFromUrl = async (websiteId: string, url: string): Promise<any> => {
  const response = await apiClient.post(API_ENDPOINTS.PUBLISHER.ARTICLES.AUTO_FROM_URL, { websiteId, url });
  return response.data;
};

export const generateArticlesFromMultiSource = async (websiteId: string, publisherPK: string): Promise<any[]> => {
  const response = await apiClient.post(API_ENDPOINTS.PUBLISHER.ARTICLES.AUTO_MULTISOURCE, { websiteId, publisherPK });
  return response.data;
};

export const getWebsitesByPublisher = async (publisherId: string) => {
  const response = await apiClient.get(API_ENDPOINTS.PUBLISHER.WEBSITES.BY_PUBLISHER(publisherId));
  return response.data;
};

// New: List IssueCasts (Optionally filter by websiteId, publisherId, limit via maxItems)
export interface IssueCastListItem {
  id: string;
  websiteId: string;
  hlsUrl: string;
  publishDate: string; // ISO date-time
  name?: string;
  description?: string;
  // NEW: live status and public URL for current live IssueCast
  isLive?: boolean;
  issueCastURL?: string | null;
  websiteBranding?: {
    name?: string;
    websiteUrl?: string;
    logoUrl?: string;
    tagline?: string;
    locationName?: string;
    backgroundGradient?: string;
  } | null;
  advertisement?: {
    name?: string;
    sponsorMention?: string;
    logoURL?: string;
    clickThroughURL?: string;
  } | null;
  weather?: {
    enabled?: boolean;
    locationName?: string | null;
    latitude?: string | number | null;
    longitude?: string | number | null;
  } | null;
  events?: {
    enabled?: boolean;
    interests?: string | null;
    usePublisherEvents?: boolean | null;
    useSearchEvents?: boolean | null;
  } | null;
  convai?: {
    enabled?: boolean;
    voiceId?: string;
    agentId?: string;
    firstMessage?: string;
  } | null;
  podcastType?: 'multiple_articles' | 'emergency';
  classification?: {
    locations?: string[];
    severity_tags?: string[];
    incident_types?: string[];
    timestamp?: string;
  } | null;
  favicons?: string[];
  podcasts?: Array<{
    type: string;
    url: string;
  }>;
  createdAt: string;
  updatedAt: string;
}
export const listIssueCasts = async (params?: { websiteId?: string; publisherId?: string; maxItems?: number }): Promise<IssueCastListItem[]> => {
  const response = await apiClient.get(API_ENDPOINTS.PUBLISHER.ISSUECASTS.LIST, {
    params: {
      websiteId: params?.websiteId,
      publisherId: params?.publisherId,
      maxItems: params?.maxItems,
    },
  });
  return response.data as IssueCastListItem[];
};

// New: IssueCast update/delete APIs
export type IssueCastUpdates = {
  hlsUrl?: string;
  publishDate?: string; // ISO date-time
  name?: string;
  description?: string;
  websiteBranding?: {
    name?: string;
    websiteUrl?: string;
    logoUrl?: string;
    tagline?: string;
    locationName?: string;
    backgroundGradient?: string;
  } | null;
  advertisement?: {
    name?: string;
    sponsorMention?: string;
    logoURL?: string;
    clickThroughURL?: string;
  } | null;
  weather?: {
    enabled?: boolean;
    locationName?: string | null;
    latitude?: string | number | null;
    longitude?: string | number | null;
  } | null;
  events?: {
    enabled?: boolean;
    interests?: string | null;
    usePublisherEvents?: boolean | null;
    useSearchEvents?: boolean | null;
  } | null;
  convai?: {
    enabled?: boolean;
    voiceId?: string;
    agentId?: string;
    firstMessage?: string;
  } | null;
  podcastType?: 'multiple_articles' | 'emergency';
  classification?: {
    locations?: string[];
    severity_tags?: string[];
    incident_types?: string[];
    timestamp?: string;
  } | null;
  favicons?: string[];
  podcasts?: Array<{
    type: string;
    url: string;
  }>;
};

export const updateIssueCast = async (id: string, updates: IssueCastUpdates): Promise<IssueCastListItem> => {
  const response = await apiClient.patch(API_ENDPOINTS.PUBLISHER.ISSUECASTS.UPDATE(id), {
    issueCastUpdates: updates,
  });
  return response.data as IssueCastListItem;
};

export const deleteIssueCast = async (id: string): Promise<void> => {
  await apiClient.delete(API_ENDPOINTS.PUBLISHER.ISSUECASTS.DELETE(id));
};

// New: Get IssueCast by ID
export interface IssueCastDetail extends IssueCastListItem {
  // Allow extra fields the server may include
  [key: string]: any;
}

export const getIssueCast = async (id: string): Promise<IssueCastDetail> => {
  const response = await apiClient.get(API_ENDPOINTS.PUBLISHER.ISSUECASTS.DETAIL(id));
  return response.data as IssueCastDetail;
};

// New: Analytics API
export interface DailyProgramMetrics {
  program_id: string;
  day: string;
  unique_viewers: number;
  minutes_est: number;
  manifest_requests: number;
  segment_requests: number;
  manifest_cache_hit_rate: number;
  segment_cache_hit_rate: number;
}

export interface AnalyticsPagination {
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface AnalyticsResponse {
  success: boolean;
  data: DailyProgramMetrics[];
  pagination?: AnalyticsPagination;
}

export interface AnalyticsRangeResponse {
  success: boolean;
  data: Record<string, DailyProgramMetrics[]> | Array<{
    program_id: string;
    total_unique_viewers: number;
    total_minutes: number;
    total_manifest_requests: number;
    total_segment_requests: number;
    avg_manifest_cache_hit_rate: number;
    avg_segment_cache_hit_rate: number;
    days_active: number;
    first_day: string;
    last_day: string;
  }>;
}

export const getLatestAnalytics = async (params?: {
  programId?: string;
  sortBy?: string;
  order?: 'asc' | 'desc';
  page?: number;
  pageSize?: number;
}): Promise<AnalyticsResponse> => {
  const response = await apiClient.get(API_ENDPOINTS.PUBLISHER.ANALYTICS.LATEST, { params });
  return response.data as AnalyticsResponse;
};

export const getAnalyticsByDay = async (day: string, params?: {
  programId?: string;
  sortBy?: string;
  order?: 'asc' | 'desc';
  page?: number;
  pageSize?: number;
}): Promise<AnalyticsResponse> => {
  const response = await apiClient.get(API_ENDPOINTS.PUBLISHER.ANALYTICS.DAY(day), { params });
  return response.data as AnalyticsResponse;
};

export const getAnalyticsRange = async (params: {
  start: string;
  end: string;
  programId?: string;
  aggregate?: boolean;
  sortBy?: string;
  order?: 'asc' | 'desc';
}): Promise<AnalyticsRangeResponse> => {
  const response = await apiClient.get(API_ENDPOINTS.PUBLISHER.ANALYTICS.RANGE, { params });
  return response.data as AnalyticsRangeResponse;
};

// New: Public IssueCast capture GET by id (returns metadata + daily forecast)
export interface IssueCastCapture {
  id: string;
  title: string;
  description: string;
  status: 'pending' | 'processing' | 'ready' | 'failed';
  hlsUrl: string;
  durationSeconds?: number | null;
  createdAt: string;
  updatedAt: string;
  [key: string]: any;
}

export interface IssueCastCaptureResponse {
  issueCast: IssueCastCapture;
  dailyForecast?: any;
}

export const getIssueCastCapture = async (id: string): Promise<IssueCastCaptureResponse> => {
  const response = await apiClient.get(API_ENDPOINTS.NOAUTH.ISSUECAST_BY_ID, { params: { id } });
  return response.data as IssueCastCaptureResponse;
};

export interface GenerateEpisodeDescriptionRequest {
  name?: string;
  publishDate?: string;
  locationName?: string;
  chapters?: any[];
  articleIds?: string[];
  hlsUrl?: string;
}

export const generateEpisodeDescription = async (
  websiteId: string,
  data: GenerateEpisodeDescriptionRequest
): Promise<{ description?: string; error?: string }> => {
  try {
    const response = await apiClient.post(
      API_ENDPOINTS.PUBLISHER.WEBSITES.STUDIO.AI_EPISODE_DESCRIPTION(websiteId),
      data
    );
    return response.data;
  } catch (error: any) {
    if (error.response && error.response.data && error.response.data.error) {
      return { error: error.response.data.error };
    }
    throw error;
  }
};

export default apiClient;